import { motion } from "framer-motion";
import { CheckCircle2, Eye, Target, Zap, Shield, Palette } from "lucide-react";
import { useState, useEffect } from "react";
import { ContactModal } from "@/components/ContactModal";
import { useScrollAnimations } from "@/components/GSAPScrollAnimations";

const WhatMakesGoodLogo = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollContainer = useScrollAnimations();

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "What Makes a Good Logo",
      "description": "Learn the essential qualities of effective logos including simplicity, memorability, versatility, and relevance. Discover what makes a logo truly great.",
      "author": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "url": "https://koech-creatives.onrender.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "logo": {
          "@type": "ImageObject",
          "url": "https://koech-creatives.onrender.com/og-image.png"
        }
      },
      "datePublished": "2026-07-13",
      "dateModified": "2026-07-13",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://koech-creatives.onrender.com/what-makes-a-good-logo"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <main ref={scrollContainer} className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            What Makes a Good Logo?
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            className="text-xl text-muted-foreground mb-8"
          >
            Discover the essential qualities that separate memorable logos from forgettable ones. Learn the principles of effective logo design that build brand recognition and stand the test of time.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get Your Logo Designed
          </motion.button>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            The Fundamentals of Great Logo Design
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            A good logo is more than just a pretty picture—it's a strategic business asset that communicates your brand's identity, values, and promise in a single glance. The most successful logos share specific qualities that make them effective, memorable, and versatile across all applications.
          </motion.p>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground"
          >
            Whether you're designing a logo for a startup or rebranding an established company, understanding these fundamental principles will help you create a logo that not only looks great but also serves your business goals effectively.
          </motion.p>
        </div>
      </section>

      {/* Core Qualities */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            6 Essential Qualities of a Good Logo
          </motion.h2>
          <div className="space-y-8">
            {[
              { 
                icon: Eye, 
                title: "Simplicity", 
                desc: "The best logos are simple and easy to recognize. A simple design is more memorable, versatile, and easier to reproduce across different mediums. Think of Apple's apple or Nike's swoosh—clean, uncomplicated designs that are instantly recognizable.",
                examples: "Apple, Nike, McDonald's"
              },
              { 
                icon: Target, 
                title: "Memorability", 
                desc: "A good logo should be memorable and distinctive. It should stand out from competitors and leave a lasting impression. Unique shapes, clever use of negative space, and unexpected elements can make your logo unforgettable.",
                examples: "FedEx, Amazon, Target"
              },
              { 
                icon: Zap, 
                title: "Versatility", 
                desc: "Your logo must work across all applications—from business cards to billboards, from websites to mobile apps. It should be effective in color and black and white, at any size, and in any context.",
                examples: "Coca-Cola, IBM, Google"
              },
              { 
                icon: Palette, 
                title: "Appropriateness", 
                desc: "A good logo reflects your brand's personality, industry, and target audience. A playful logo works for a toy company but not for a law firm. The design should align with your brand positioning.",
                examples: "Playboy, Lego, Disney"
              },
              { 
                icon: Shield, 
                title: "Timelessness", 
                desc: "Great logos avoid trendy elements that quickly become dated. They should remain relevant and effective for years, even decades. Classic designs focus on enduring principles rather than fleeting fads.",
                examples: "Chanel, Mercedes-Benz, Rolex"
              },
              { 
                icon: CheckCircle2, 
                title: "Scalability", 
                desc: "Your logo should be scalable without losing quality. It should work as a favicon on a website and as a large sign on a building. Vector-based design ensures crisp reproduction at any size.",
                examples: "Twitter, Airbnb, Spotify"
              },
            ].map((quality, index) => (
              <motion.div
                key={quality.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <div className="flex gap-4 mb-4">
                  <quality.icon className="w-10 h-10 text-coral flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-xl mb-2">{quality.title}</h3>
                    <p className="text-muted-foreground mb-3">{quality.desc}</p>
                    <p className="text-sm text-coral font-medium">Examples: {quality.examples}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Common Logo Design Mistakes to Avoid
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { mistake: "Overcomplicating the Design", fix: "Keep it simple. Remove unnecessary elements." },
              { mistake: "Using Too Many Colors", fix: "Limit to 2-3 colors for maximum impact." },
              { mistake: "Following Trends Blindly", fix: "Focus on timeless design principles." },
              { mistake: "Ignoring Scalability", fix: "Test your logo at various sizes." },
              { mistake: "Poor Font Choices", fix: "Choose readable, appropriate typography." },
              { mistake: "Copying Competitors", fix: "Create something unique to your brand." },
            ].map((item, index) => (
              <motion.div
                key={item.mistake}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-2 text-red-400">{item.mistake}</h3>
                <p className="text-muted-foreground">{item.fix}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testing Your Logo */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            How to Test Your Logo Design
          </motion.h2>
          <div className="space-y-4">
            {[
              { test: "The Squint Test", desc: "Squint your eyes—can you still recognize the logo?" },
              { test: "The Black & White Test", desc: "Does it work without color?" },
              { test: "The Size Test", desc: "Is it readable at 1 inch and 10 feet?" },
              { test: "The Context Test", desc: "Does it work on different backgrounds?" },
              { test: "The Memory Test", desc: "Can people describe it after seeing it once?" },
              { test: "The Relevance Test", desc: "Does it fit your industry and audience?" },
            ].map((item, index) => (
              <motion.div
                key={item.test}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <CheckCircle2 className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.test}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional vs DIY */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Professional Logo Design vs DIY
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="bg-card p-6 rounded-lg border border-gray-800"
            >
              <h3 className="font-semibold text-xl mb-4 text-coral">Professional Design</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Strategic approach</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Original, unique design</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Professional expertise</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> All file formats included</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Brand guidelines provided</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Revisions and refinements</li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              
              className="bg-card p-6 rounded-lg border border-gray-800"
            >
              <h3 className="font-semibold text-xl mb-4 text-muted-foreground">DIY Design</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> Lower cost upfront</li>
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> Creative control</li>
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> Quick turnaround possible</li>
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> Learning opportunity</li>
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> Unlimited iterations</li>
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> No dependency on others</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Related Resources
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Ultimate Guide to Logo Design Services", url: "/logo-design-services-guide", desc: "Comprehensive guide to professional logo design" },
              { title: "Logo Design Principles", url: "/logo-design-principles", desc: "Master the fundamental design principles" },
              { title: "Types of Logos Explained", url: "/types-of-logos-explained", desc: "Explore different logo types and when to use them" },
              { title: "How to Design a Logo", url: "/how-to-design-a-logo", desc: "Step-by-step logo design process" },
            ].map((resource, index) => (
              <motion.a
                key={resource.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                href={resource.url}
                className="bg-card p-6 rounded-lg border border-gray-800 hover:border-coral transition-colors block"
              >
                <h3 className="font-semibold text-lg mb-2">{resource.title}</h3>
                <p className="text-muted-foreground">{resource.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Need a Professional Logo?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-xl text-muted-foreground mb-8"
          >
            Our expert designers create logos that embody these essential qualities and drive real business results.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Start Your Logo Project
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default WhatMakesGoodLogo;
